import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import GromlySplash from './components/brand/GromlySplash';
import AppShell from './components/layout/AppShell';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import { AdminOnly, HomeRoute, StaffOnly } from './components/layout/AccountRoutes';
import TripDetailPage from './pages/trip/TripDetailPage';
import ItineraryTab from './pages/trip/ItineraryTab';
import VenuesTab from './pages/trip/VenuesTab';
import ExpensesTab from './pages/trip/ExpensesTab';
import InfoTab from './pages/trip/InfoTab';
import PackingTab from './pages/trip/PackingTab';
import MealsTab from './pages/trip/MealsTab';
import NotesTab from './pages/trip/NotesTab';
import ServicesTab from './pages/trip/ServicesTab';
import MapTab from './pages/trip/MapTab';
import BookingsTab from './pages/trip/BookingsTab';
import JournalTab from './pages/trip/JournalTab';
import AlbumTab from './pages/trip/AlbumTab';
import SharedTripPage from './pages/share/SharedTripPage';
import DocsPage from './pages/docs/DocsPage';
import ServicesPage from './pages/services/ServicesPage';
import ExplorePage from './pages/explore/ExplorePage';
import CommunityTripDetailPage from './pages/explore/CommunityTripDetailPage';
import AuthCallbackPage from './pages/auth/AuthCallbackPage';
import AccountPage from './pages/account/AccountPage';
import LoadingScreen from './components/ui/LoadingScreen';
import PrivacyPage from './pages/legal/PrivacyPage';
import TermsPage from './pages/legal/TermsPage';
import LegalNoticePage from './pages/legal/LegalNoticePage';
import AcceptInvitePage from './pages/invite/AcceptInvitePage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import CompleteProfilePage from './pages/auth/CompleteProfilePage';
import LandingPage from './pages/LandingPage';

// Screens only admins and agency staff open load on demand: the main chunk is
// precached by the PWA and sits at its 4 MiB limit (see PENDING.md), and the
// metrics charts alone are most of what these pages weigh.
const AdminUsagePage = lazy(() => import('./pages/admin/AdminUsagePage'));
const AdminAgenciesPage = lazy(() => import('./pages/account/AdminAgenciesPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminUserPage = lazy(() => import('./pages/admin/AdminUserPage'));
const AgencyPage = lazy(() => import('./pages/agency/AgencyPage'));
const AgencyInvitePage = lazy(() => import('./pages/agency/AgencyInvitePage'));

function OnDemand({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LoadingScreen />}>{children}</Suspense>;
}

function ProtectedRoute() {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <GromlySplash />;
  }

  if (!isAuthenticated) {
    // With the query: an invitation link (?token=) must survive the sign-in.
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  // Onboarding guard: authenticated users without terms acceptance (e.g. Google
  // OAuth first-time sign-in) must finish setup before accessing the app.
  if (user && !user.termsAcceptedAt && location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" replace />;
  }

  return <Outlet />;
}

function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <GromlySplash />;
  }

  const redirect = new URLSearchParams(location.search).get('redirect');
  const target = redirect && redirect.startsWith('/') ? redirect : '/dashboard';
  return isAuthenticated ? <Navigate to={target} replace /> : <Outlet />;
}

export default function Router() {
  return (
    <Routes>
      <Route element={<PublicRoute />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route path="/complete-profile" element={<CompleteProfilePage />} />
        <Route element={<AppShell />}>
          <Route path="/dashboard" element={<HomeRoute />} />
          <Route path="/account" element={<AccountPage />} />
          <Route element={<AdminOnly />}>
            <Route path="/admin/users" element={<OnDemand><AdminUsersPage /></OnDemand>} />
            <Route path="/admin/users/:userId" element={<OnDemand><AdminUserPage /></OnDemand>} />
            <Route path="/admin/agencies" element={<OnDemand><AdminAgenciesPage /></OnDemand>} />
            <Route path="/admin/metrics" element={<OnDemand><AdminUsagePage /></OnDemand>} />
          </Route>
          <Route element={<StaffOnly />}>
            <Route path="/agency/:agencyId/*" element={<OnDemand><AgencyPage /></OnDemand>} />
          </Route>
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/explore/:tripId" element={<CommunityTripDetailPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/agency-invite" element={<OnDemand><AgencyInvitePage /></OnDemand>} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/trips/:tripId" element={<TripDetailPage />}>
            <Route index element={<Navigate to="itinerary" replace />} />
            <Route path="itinerary" element={<ItineraryTab />} />
            <Route path="venues" element={<VenuesTab />} />
            <Route path="map" element={<MapTab />} />
            <Route path="bookings" element={<BookingsTab />} />
            <Route path="expenses" element={<ExpensesTab />} />
            <Route path="meals" element={<MealsTab />} />
            <Route path="packing" element={<PackingTab />} />
            <Route path="info" element={<InfoTab />} />
            <Route path="notes" element={<NotesTab />} />
            <Route path="services" element={<ServicesTab />} />
            <Route path="gallery" element={<JournalTab />} />
            <Route path="album" element={<AlbumTab />} />
          </Route>
        </Route>
      </Route>

      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* Outside PublicRoute on purpose: the link arrives by email and must work
          even in a browser that still has a live session. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite/accept" element={<AcceptInvitePage />} />
      <Route path="/share/:token" element={<SharedTripPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/legal" element={<LegalNoticePage />} />
      <Route path="/aviso-legal" element={<Navigate to="/legal" replace />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />{/* authenticated fallback — handled by PublicRoute above */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
